import { getSessionStorageOrDefault } from "../hooks/useSessionStorage";
import { SESSION_USERNAME_KEY } from "./constants";

/**
 * The session username carries the identifier from the identifier page to the password page and
 * the account linking pages. sessionStorage is the only copy: every reader goes through
 * `getSessionUsername` (or `useSessionUsername`, which subscribes to this store), and every writer
 * goes through the functions below so subscribed components re-render. Do not keep the value in
 * component state.
 */
const listeners = new Set<() => void>();

// A listener that throws must not stop the others, or make a write that succeeded look failed.
const notify = () =>
  listeners.forEach((listener) => {
    try {
      listener();
    } catch (e) {
      console.error("a session username listener threw", e);
    }
  });

export const subscribeToSessionUsername = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

// useSyncExternalStore needs getSnapshot to return the same value until the store changes. Only
// strings are stable here (JSON.parse returns a new object on every call), and only strings are
// ever written, so anything else under the key is treated as no username.
export const getSessionUsername = (): string => {
  const stored = getSessionStorageOrDefault<unknown>(SESSION_USERNAME_KEY, "");
  return typeof stored === "string" ? stored : "";
};

export const setSessionUsername = (username: string) => {
  if (username === null || username === undefined) {
    throw new Error("Call removeSessionUsername if you want to remove this value");
  }
  try {
    sessionStorage.setItem(SESSION_USERNAME_KEY, JSON.stringify(username));
  } catch (e) {
    console.warn(`failed to set ${SESSION_USERNAME_KEY} session storage item`, e);
    return;
  }
  notify();
};

export const removeSessionUsername = () => {
  try {
    sessionStorage.removeItem(SESSION_USERNAME_KEY);
  } catch (e) {
    console.warn(`failed to remove ${SESSION_USERNAME_KEY} session storage item`, e);
    return;
  }
  notify();
};
